import type { Attachment, Author } from "@/lib/types";

export function authorName(a: Author) {
  if (a.literal) return a.literal;
  return [a.given, a.family].filter(Boolean).join(" ");
}

export function formatAuthorsShort(authors: Author[] | null | undefined) {
  if (!authors?.length) return "";
  const fam = (a: Author) => a.family ?? a.literal ?? a.given ?? "";
  if (authors.length === 1) return fam(authors[0]);
  if (authors.length === 2) return `${fam(authors[0])} & ${fam(authors[1])}`;
  return `${fam(authors[0])} et al.`;
}

export function formatAuthorsFull(authors: Author[] | null | undefined) {
  return (authors ?? []).map(authorName).join("; ");
}

/** Parse "Family, Given; Family2, Given2" into structured authors. */
export function parseAuthors(text: string): Author[] {
  return text
    .split(/;|\n/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => {
      if (s.includes(",")) {
        const [family, ...rest] = s.split(",");
        return { family: family.trim(), given: rest.join(",").trim() || undefined };
      }
      const parts = s.split(/\s+/);
      if (parts.length === 1) return { literal: s };
      return { family: parts[parts.length - 1], given: parts.slice(0, -1).join(" ") };
    });
}

export function authorsToText(authors: Author[] | null | undefined) {
  return (authors ?? []).map((a) => (a.literal ? a.literal : [a.family, a.given].filter(Boolean).join(", "))).join("; ");
}

export function bytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}


export function displayTitle(item: { title: string; attachments?: { filename: string }[] }) {
  if (item.title?.trim()) return item.title;
  const f = item.attachments?.[0]?.filename;
  return f ? `Untitled source (file: ${f})` : "Untitled source";
}

/** Text-first description of a PDF's extraction state. */
export function extractionLabel(att: Pick<Attachment, "extractionStatus" | "pageCount" | "mime"> | null | undefined) {
  if (!att) return "No PDF";
  if (att.mime !== "application/pdf") return "File attached";
  switch (att.extractionStatus) {
    case "queued":
      return "PDF: reading the text soon…";
    case "processing":
      return "PDF: reading the text…";
    case "done":
      return `PDF: text extracted (${att.pageCount ?? "?"} pages)`;
    case "partial":
      return `PDF: partial text (${att.pageCount ?? "?"} pages, some without text)`;
    case "no_text":
      return "PDF: this looks like a scan; its pages can be read with AI";
    case "encrypted":
      return "PDF: password-protected; its text cannot be read";
    case "malformed":
      return "PDF: damaged or invalid file";
    case "failed":
      return "PDF: the text could not be read";
    default:
      return "PDF attached";
  }
}

export function extractionTone(status: string | null | undefined): "ok" | "warn" | "error" | "busy" {
  if (status === "done") return "ok";
  if (status === "queued" || status === "processing") return "busy";
  if (status === "partial" || status === "no_text") return "warn";
  return status ? "error" : "warn";
}

export function primaryPdf(atts: Attachment[] | undefined) {
  // Library attachments are PDFs; encrypted ones carry a placeholder type until decrypted.
  return (atts ?? []).find((a) => a.mime === "application/pdf" || a.encrypted) ?? null;
}
